/* eslint-disable @typescript-eslint/no-empty-function */
import jwtDecode from 'jwt-decode'
import React, { createContext, useEffect, useState } from 'react'
import ApiService from '../services/ApiService'
import { UserProviderProps } from '../types/globalTypes'
import { validateToken } from '../utils/tokenValidation'

interface UserContextProps {
  user: DecodedUser
  setUser: (user: DecodedUser) => void
  token: string
  setToken: (token: string) => void
  googleEmail: string
  setGoogleEmail: (email: string) => void
  googlePicture: string
  setGooglePicture: (photo: string) => void
  googleName: string
  setGoogleName: (name: string) => void
  isLoadingAuth: boolean
  setIsLoadingAuth: (loading: boolean) => void
}

export enum Roles {
  globalAdmin = "ADMIN_GLOBAL",
  tenantAdmin = "TENANT_ADMIN",
  tenantUser = "TENANT_USER"
}

interface DecodedUser {
  map: any
  status: number
  id: string
  name: string
  email: string
  cnpj: string
  adminUser: boolean
  picture: string
  approved: string
  sub: number
  profileId: number
  permissions: string[]
  role: Roles
  tenantId: number
}

const UserContext = createContext<UserContextProps>({
  user: {} as DecodedUser,
  setUser: () => { },
  token: '',
  setToken: () => { },
  googleEmail: '',
  setGoogleEmail: () => { },
  googlePicture: '',
  setGooglePicture: () => { },
  googleName: '',
  setGoogleName: () => { },
  isLoadingAuth: true,
  setIsLoadingAuth: () => { },
})

export default UserContext

export const UserProvider: React.FC<UserProviderProps> = ({ children }) => {
  const [user, setUser] = useState<DecodedUser>({} as DecodedUser)
  const [token, setToken] = useState<string>('')
  const [googleEmail, setGoogleEmail] = useState<string>('')
  const [googlePicture, setGooglePicture] = useState<string>('')
  const [googleName, setGoogleName] = useState<string>('')
  const [isLoadingAuth, setIsLoadingAuth] = useState<boolean>(true)

  const setUserWithLogging = (newUser: DecodedUser) => {
    console.log('📝 [USER-CONTEXT] Atualizando usuário no contexto:', {
      previousUser: {
        id: user?.id || user?.sub,
        email: user?.email,
        tenantId: user?.tenantId
      },
      newUser: {
        id: newUser?.id || newUser?.sub,
        email: newUser?.email,
        name: newUser?.name,
        tenantId: newUser?.tenantId,
        approved: newUser?.approved,
        permissionsCount: newUser?.permissions?.length
      },
      timestamp: new Date().toISOString()
    })
    setUser(newUser)
  }

  const setTokenWithLogging = (newToken: string) => {
    console.log('📝 [USER-CONTEXT] Atualizando token:', {
      hasToken: !!newToken,
      tokenLength: newToken?.length,
      tokenPreview: newToken ? `${newToken.substring(0, 50)}...` : null,
      timestamp: new Date().toISOString()
    })
    setToken(newToken)
  }

  useEffect(() => {
    console.log('🔑 [USER-CONTEXT] Verificando token armazenado no localStorage...')
    setIsLoadingAuth(true)
    const storedToken = localStorage.getItem('token')

    console.log('🔑 [USER-CONTEXT] Token no localStorage:', {
      hasStoredToken: !!storedToken,
      tokenLength: storedToken?.length,
      tokenPreview: storedToken ? `${storedToken.substring(0, 50)}...` : null
    })

    if (storedToken) {
      console.log('🔑 [USER-CONTEXT] Validando token armazenado...')
      const validation = validateToken(storedToken)

      console.log('✅ [USER-CONTEXT] Resultado da validação:', {
        isValid: validation.isValid,
        hasDecodedToken: !!validation.decodedToken,
        tenantId: validation.tenantId
      })

      if (validation.isValid && validation.decodedToken) {
        (async () => {
          try {
            const userId = validation.decodedToken.sub
            const userRes = await ApiService.user.findById(Number(userId))
            if (userRes.status === 200 && userRes.data) {
              const fetchedUser = userRes.data
              if (fetchedUser.approved && fetchedUser.approved === 'approved') {
                setToken(storedToken)
                setUser(fetchedUser)
              } else {
                localStorage.removeItem('token')
              }
            } else {
              localStorage.removeItem('token')
            }
          } catch (err) {
            localStorage.removeItem('token')
          } finally {
            setIsLoadingAuth(false)
          }
        })()
      } else {
        console.warn('⚠️ [USER-CONTEXT] Token inválido, removendo do localStorage')
        localStorage.removeItem('token')
        setIsLoadingAuth(false)
      }
    } else {
      console.log('🔑 [USER-CONTEXT] Nenhum token encontrado no localStorage')
      setIsLoadingAuth(false)
    }
  }, [])

  useEffect(() => {
    console.log('🔑 [USER-CONTEXT] Token mudou, verificando se precisa decodificar:', {
      hasToken: !!token,
      hasUser: !!(user?.id || user?.sub),
      userId: user?.id || user?.sub
    })

    if (token && (!user || (!user.id && !user.sub))) {
      console.log('🔑 [USER-CONTEXT] Decodificando token para extrair identificadores e buscar usuário...')
      try {
        const decoded = jwtDecode(token) as any
        const userId = decoded.sub || decoded.id
        const profileId = decoded.profileId

        console.log('🔍 [USER-CONTEXT] Identificadores obtidos do token:', { userId, profileId, tenantId: decoded.tenantId })

        if (userId) {
          ApiService.user
            .findById(Number(userId))
            .then((res) => {
              if (res && res.data) {
                setUser(res.data)
                console.log('✅ [USER-CONTEXT] Usuário carregado via API:', { id: res.data.id })
              } else {
                console.warn('⚠️ [USER-CONTEXT] Resposta vazia ao buscar usuário via API')
              }
            })
            .catch((err) => {
              console.error('❌ [USER-CONTEXT] Erro ao buscar usuário via API:', err)
              localStorage.removeItem('token')
              setToken('')
              setUser({} as DecodedUser)
            })
        } else {
          console.warn('⚠️ [USER-CONTEXT] Token não contém sub/id válido, removendo token')
          localStorage.removeItem('token')
          setToken('')
          setUser({} as DecodedUser)
        }
      } catch (error) {
        console.error('❌ [USER-CONTEXT] Erro ao decodificar token:', error)
        localStorage.removeItem('token')
        setToken('')
        setUser({} as DecodedUser)
      }
    }
  }, [token, user?.id, user?.sub])

  return (
    <UserContext.Provider
      value={{
        user,
        setUser: setUserWithLogging,
        token,
        setToken: setTokenWithLogging,
        googleEmail,
        setGoogleEmail,
        googlePicture,
        setGooglePicture,
        googleName,
        setGoogleName,
        isLoadingAuth,
        setIsLoadingAuth,
      }}
    >
      {children}
    </UserContext.Provider>
  )
}
